import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import Input from '../../atoms/Input';
import Spinner from '../../atoms/Spinner';
import TypeaheadProps, { Option } from './Typeahead.types';
import useDebounce from '../../shared/utils/debounceHook';
import styles from './Typeahead.module.scss';

const detectKey = (event: KeyboardEvent) => {
  const isSpace = event.key === ' ' || event.key === 'Spacebar';
  const isEnter = event.key === 'Enter';
  const isEscape = event.key === 'Escape';
  const up = event.key === 'ArrowUp';
  const down = event.key === 'ArrowDown';

  return { isSpace, isEnter, isEscape, up, down };
};

const Typeahead = ({
  search = '',
  onChange = () => {},
  options: defaultOptions = [],
  startText,
  noMatchText,
  loadingText,
  filterFunction,
  minSearchLength = 0,
  dataPointer,
  debounceDelay = 0,
  errorMessage,
  dataCy,
  placeholder,
  invalid,
  ariaLabel,
  errorMsgHolderId,
}: TypeaheadProps) => {
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string | null>(search);
  const [hasError, setHasError] = useState<boolean>(false);
  const [options, setOptions] = useState<any>(defaultOptions);
  const [loading, setLoading] = useState<boolean>(false);
  const typeaheadElement = useRef<HTMLDivElement>(null);
  const selectionMade = useRef<boolean>(false);

  const scrollToIndex = (newIndex: number, highlight = true) => {
    if (highlight) setHighlightedIndex(newIndex);
    if (typeaheadElement.current) {
      const optionsList = typeaheadElement.current.querySelector(`#searchItem_${newIndex}`);
      if (optionsList) optionsList.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleKeyDown = (event) => {
    const { isSpace, isEnter, isEscape, up, down } = detectKey(event);
    setOptionsOpen(true);
    selectionMade.current = false;

    if (down) {
      event.preventDefault();
      if (highlightedIndex + 1 < options.length) {
        const newIndex = highlightedIndex + 1;
        scrollToIndex(newIndex);
      }
    }

    if (up) {
      event.preventDefault();
      if (highlightedIndex > 0) {
        const newIndex = highlightedIndex - 1;
        scrollToIndex(newIndex);
      }
    }

    if ((isEnter || isSpace) && highlightedIndex >= 0) {
      setSelectedIndex(highlightedIndex);
      event.preventDefault();
    }

    if (isEscape) {
      setOptionsOpen(false);
    }
  };

  const handleSearch = (event) => {
    setSearchString(event.currentTarget.value);
  };

  const handleFocus = (event) => {
    setOptionsOpen(true);
    event.currentTarget.setSelectionRange(-1, -1);
  };

  const handleClick = (event) => {
    setSelectedIndex(Number(event.target.getAttribute('aria-posinset')));
    typeaheadElement.current.focus();
  };

  const handleMouseOver = (event) => {
    setHighlightedIndex(Number(event.target.getAttribute('aria-posinset')));
  };

  const customFilter = () => {
    setOptionsOpen(true);

    const filteredOptions = filterFunction(searchString, defaultOptions, options[selectedIndex]);
    filteredOptions
      .then((res) => {
        setLoading(false);
        setOptions(res);
        setHasError(false);
      })
      .catch(() => {
        setLoading(false);
        setOptions([]);
        setHasError(true);
      });
  };

  const buildOnChangeReturn = (dataPointer): { dataPointer: string; value: any } | Option => {
    // if dataPointer has been provided it is in a Form compoment and should return the object
    // otherwise just the option value
    if (!dataPointer) return options[selectedIndex];

    // to ensure compatability with the JSON schema a value can be passed in as a const, title or simply the array item
    const optionValue = options[selectedIndex]?.const || options[selectedIndex]?.title || options[selectedIndex];

    return {
      dataPointer,
      value: optionValue,
    };
  };

  useEffect(() => {
    setLoading(true);
  }, [searchString]);

  const debouncedSearchString = useDebounce(searchString, debounceDelay);

  useEffect(() => {
    if (filterFunction && debouncedSearchString?.length >= minSearchLength && !selectionMade.current) {
      setLoading(true);
      customFilter();
    } else if (debouncedSearchString?.length && !selectionMade.current) {
      setLoading(true);
      const filteredOptions = defaultOptions.filter((option: Option) => {
        let value: string;
        if (typeof option === 'string') {
          value = option;
        } else if (typeof option.title === 'string') {
          value = option?.title;
        } else {
          value = option?.title?.innerText;
        }
        return value.toLowerCase().includes(debouncedSearchString.toLowerCase());
      });
      setLoading(false);
      setOptions(filteredOptions);
    } else if (!selectionMade.current) {
      setLoading(false);
      setOptions(defaultOptions);
    }

    setOptionsOpen(true);
  }, [debouncedSearchString]);

  useEffect(() => {
    if (selectedIndex > -1) {
      if (typeof options[selectedIndex] === 'object' && options[selectedIndex]?.params?.continueFiltering) {
        setLoading(true);
        customFilter();
      } else {
        selectionMade.current = true;
        const value =
          options[selectedIndex]?.params?.textDescription || options[selectedIndex].title || options[selectedIndex];
        if (typeof value === 'string') {
          setSearchString(value);
          setOptionsOpen(false);
        }

        const onChangeValue = buildOnChangeReturn(dataPointer);
        onChange(onChangeValue);
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    setHighlightedIndex(options.length === 1 ? 0 : -1);
    setSelectedIndex(-1);
  }, [options]);

  useLayoutEffect(() => {
    if (options) scrollToIndex(0, false);
  }, [options]);
  return (
    <div
      className={cx(styles.typeahead)}
      ref={typeaheadElement}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      role='searchbox'>
      <Input
        controlled
        value={searchString}
        onChange={handleSearch}
        ariaProps={{
          'aria-expanded': optionsOpen,
          'aria-owns': 'typeahead__listbox',
          'aria-autocomplete': 'list',
        }}
        label={ariaLabel}
        autoComplete='new-password'
        onFocus={handleFocus}
        className={styles.dropDownInput}
        dataCy={dataCy}
        placeholder={placeholder}
        invalid={invalid}
        errorMsgHolderId={errorMsgHolderId}
      />
      <ul id='typeahead__listbox' role='listbox' className={styles.listbox}>
        {hasError && errorMessage && (
          <li
            className={cx(styles.option, styles.emptyOption, styles.inactive)}
            aria-selected='false'
            role='option'
            id='start'
            tabIndex={-1}>
            {errorMessage}
          </li>
        )}
        {loading && loadingText && !hasError && !selectionMade.current && (
          <li
            className={cx(styles.option, styles.emptyOption, styles.inactive)}
            aria-selected='false'
            role='option'
            id='start'
            tabIndex={-1}>
            <span className={styles.spinnerContainer}>
              <Spinner />
            </span>
            {loadingText}
          </li>
        )}
        {optionsOpen &&
          options &&
          !loading &&
          !!options.length &&
          searchString.length >= minSearchLength &&
          options.map((option, index) => (
            // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events, jsx-a11y/click-events-have-key-events
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={`${searchString}_${index}`}
              id={`searchItem_${index}`}
              className={cx(styles.option, {
                [styles.isSelected]: highlightedIndex === index,
              })}
              aria-selected={highlightedIndex === index}
              role='option'
              tabIndex={-1}
              aria-posinset={index}
              aria-setsize={options.length}
              onClick={handleClick}
              onMouseOver={handleMouseOver}>
              {option.title || option}
            </li>
          ))}
        {optionsOpen && startText && searchString.length < minSearchLength && !loading && !hasError && (
          <li
            className={cx(styles.option, styles.emptyOption, styles.inactive)}
            aria-selected='false'
            role='option'
            id='start'
            tabIndex={-1}>
            {startText}
          </li>
        )}
        {optionsOpen &&
          noMatchText &&
          !hasError &&
          !options.length &&
          !loading &&
          !selectionMade.current &&
          searchString.length > minSearchLength && (
            <li
              className={cx(styles.option, styles.emptyOption, styles.inactive, styles.noMatch)}
              aria-selected='false'
              role='option'
              id='noMatch'
              tabIndex={-1}>
              {noMatchText}
            </li>
          )}
      </ul>
    </div>
  );
};

export default Typeahead;
